import { useCallback, useEffect, useRef, useState } from 'react'

import { NEW_STORES_DEV, SAME_STORES_DEV } from '@/constants'
import { Region } from '@/types'

// ----------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------

export type BranchType = 'all' | 'same' | 'new' | 'regular'

export type Selection = {
  selectedRegions: number[]
  selectedBranches: string[]
  selectedBranchNames: string[]
  branchType: BranchType
  predefinedSelection?: 'all' | 'same' | 'new' | null
}

export interface BasicBranchSelectProps {
  onChange?: (selection: Selection) => void
  value?: string
  branchType?: BranchType
  resetBranchSelect?: React.MutableRefObject<boolean>
  branchOnly?: boolean
}

// ----------------------------------------------------------------------
// HELPER FUNCTIONS
// ----------------------------------------------------------------------

export const calculateFullySelectedRegions = (
  branchIds: string[],
  regions: Region[]
) => {
  return regions
    .filter((region) => {
      if (region.branches.length === 0) return false
      return region.branches.every((branch) => branchIds.includes(branch.id))
    })
    .map((region) => region.id)
}

export const findBranch = (branchId: string, regions: Region[]) =>
  regions.flatMap((r) => r.branches).find((b) => b.id === branchId)

export const findRegion = (regionId: number, regions: Region[]) =>
  regions.find((r) => r.id === regionId)

export const findBranchRegion = (branchId: string, regions: Region[]) =>
  regions.find((r) => r.branches.some((b) => b.id === branchId))

// Detect if the current selection matches a predefined set (All/Same/New)
export const detectPredefinedSelection = (
  branchNames: string[],
  selectableRegions: Region[]
): 'all' | 'same' | 'new' | null => {
  if (branchNames.length === 0) return null

  const allBranches = selectableRegions.flatMap((r) =>
    r.branches.map((b) => b.name)
  )

  const sameBranches = SAME_STORES_DEV
  const newBranches = NEW_STORES_DEV

  const sortedSelection = [...branchNames].sort()
  const sortedAll = [...allBranches].sort()
  const sortedSame = [...sameBranches].sort()
  const sortedNew = [...newBranches].sort()

  if (JSON.stringify(sortedSelection) === JSON.stringify(sortedAll))
    return 'all'
  if (JSON.stringify(sortedSelection) === JSON.stringify(sortedSame))
    return 'same'
  if (JSON.stringify(sortedSelection) === JSON.stringify(sortedNew))
    return 'new'

  return null
}

// Create a selection object for a predefined type
export const createPredefinedSelection = (
  type: 'all' | 'same' | 'new',
  regions: Region[]
): Selection => {
  let branches: { id: string; name: string }[] = []

  if (type === 'all') {
    branches = regions.flatMap((r) => r.branches)
  } else if (type === 'same') {
    branches = regions.flatMap((r) =>
      r.branches.filter((b) => SAME_STORES_DEV.includes(b.name))
    )
  } else if (type === 'new') {
    branches = regions.flatMap((r) =>
      r.branches.filter((b) => NEW_STORES_DEV.includes(b.name))
    )
  }

  const selectedBranchIds = branches.map((b) => b.id)

  return {
    selectedRegions: calculateFullySelectedRegions(selectedBranchIds, regions),
    selectedBranches: selectedBranchIds,
    selectedBranchNames: branches.map((b) => b.name),
    branchType: type,
    predefinedSelection: type,
  }
}

export const emptySelection: Selection = {
  selectedRegions: [],
  selectedBranches: [],
  selectedBranchNames: [],
  branchType: 'regular',
  predefinedSelection: null,
}

// ----------------------------------------------------------------------
// CUSTOM HOOK
// ----------------------------------------------------------------------

export const useBranchSelection = ({
  regions,
  selectableRegions,
  value,
  branchType,
  tenant,
  onChange,
  resetBranchSelect,
}: {
  regions: Region[]
  selectableRegions: Region[]
  value?: string
  branchType?: BranchType
  tenant: string
  onChange?: (selection: Selection) => void
  resetBranchSelect?: React.MutableRefObject<boolean>
}) => {
  const [selection, setSelection] = useState<Selection>(emptySelection)
  const [expandedRegions, setExpandedRegions] = useState<Set<number>>(new Set())
  const hasInitialized = useRef(false)

  const getDerivedSelection = useCallback((): Selection => {
    if (regions.length === 0) return emptySelection

    if (branchType === 'same')
      return createPredefinedSelection('same', selectableRegions)
    if (branchType === 'all') {
      return createPredefinedSelection('all', selectableRegions)
    }
    if (branchType === 'new')
      return createPredefinedSelection('new', selectableRegions)

    if (value) {
      const branchNames = value.split(',').map((name) => name.trim())
      const matchedBranches = regions
        .flatMap((r) => r.branches)
        .filter((b) => branchNames.includes(b.name))

      if (matchedBranches.length > 0) {
        const selectedBranchIds = matchedBranches.map((b) => b.id)
        return {
          selectedRegions: calculateFullySelectedRegions(
            selectedBranchIds,
            regions
          ),
          selectedBranches: selectedBranchIds,
          selectedBranchNames: matchedBranches.map((b) => b.name),
          branchType: 'regular',
          predefinedSelection: null,
        }
      }
    }

    if (!value && !branchType) {
      const defaultType = tenant === 'blankstreet' ? 'all' : 'same'
      if (defaultType === 'same') {
        return createPredefinedSelection('same', selectableRegions)
      }
      if (defaultType === 'all') {
        return createPredefinedSelection('all', selectableRegions)
      }
    }

    return emptySelection
  }, [regions, selectableRegions, value, branchType, tenant])

  useEffect(() => {
    if (regions.length === 0) return

    const newSelection = getDerivedSelection()

    const isDifferent =
      newSelection.branchType !== selection.branchType ||
      JSON.stringify(newSelection.selectedBranches.sort()) !==
        JSON.stringify(selection.selectedBranches.sort())

    if (isDifferent) {
      setSelection(newSelection)
      if (!hasInitialized.current) {
        hasInitialized.current = true
        if (newSelection.selectedBranches.length > 0) {
          onChange?.(newSelection)
        }
      }
    }
  }, [getDerivedSelection, regions.length])

  // Reset
  useEffect(() => {
    if (resetBranchSelect?.current && regions.length > 0) {
      const finalResetSelection = getDerivedSelection()

      setSelection(finalResetSelection)
      setExpandedRegions(new Set())
      resetBranchSelect.current = false
      onChange?.(finalResetSelection)
    }
  }, [resetBranchSelect, regions, tenant, getDerivedSelection, onChange])

  const updateSelection = useCallback(
    (newSelection: Selection) => {
      const detectedPredefined = detectPredefinedSelection(
        newSelection.selectedBranchNames,
        selectableRegions
      )

      let determinedBranchType: BranchType = 'regular'
      if (detectedPredefined) determinedBranchType = detectedPredefined

      const finalSelection = {
        ...newSelection,
        branchType: determinedBranchType,
        predefinedSelection: detectedPredefined,
      }

      setSelection(finalSelection)
      onChange?.(finalSelection)
    },
    [selectableRegions, onChange]
  )

  const handlePredefinedSelect = useCallback(
    (type: 'all' | 'same' | 'new', checked: boolean) => {
      if (checked) {
        updateSelection(createPredefinedSelection(type, selectableRegions))
      } else {
        updateSelection(emptySelection)
      }
    },
    [selectableRegions, updateSelection]
  )

  const handleRegionSelect = useCallback(
    (regionId: number, checked: boolean) => {
      const region = findRegion(regionId, regions)
      if (!region) return

      const branchIds = region.branches.map((b) => b.id)
      const branchNames = region.branches.map((b) => b.name)

      let newBranchIds: string[]
      let newBranchNames: string[]
      let newRegionIds: number[]

      if (checked) {
        // Add
        newBranchIds = [
          ...new Set([...selection.selectedBranches, ...branchIds]),
        ]
        newBranchNames = [
          ...new Set([...selection.selectedBranchNames, ...branchNames]),
        ]
        newRegionIds = [...new Set([...selection.selectedRegions, regionId])]
      } else {
        // Remove
        newBranchIds = selection.selectedBranches.filter(
          (id) => !branchIds.includes(id)
        )
        newBranchNames = selection.selectedBranchNames.filter(
          (name) => !branchNames.includes(name)
        )
        newRegionIds = selection.selectedRegions.filter((id) => id !== regionId)
      }

      updateSelection({
        selectedRegions: newRegionIds,
        selectedBranches: newBranchIds,
        selectedBranchNames: newBranchNames,
        branchType: 'regular',
        predefinedSelection: null,
      })
    },
    [selection, regions, updateSelection]
  )

  const handleBranchSelect = useCallback(
    (regionId: number, branchId: string, checked: boolean) => {
      const branch = findBranch(branchId, regions)
      if (!branch) return

      let newBranchIds: string[]
      let newBranchNames: string[]

      if (checked) {
        newBranchIds = [...selection.selectedBranches, branchId]
        newBranchNames = [...selection.selectedBranchNames, branch.name]
      } else {
        newBranchIds = selection.selectedBranches.filter(
          (id) => id !== branchId
        )
        newBranchNames = selection.selectedBranchNames.filter(
          (name) => name !== branch.name
        )
      }

      // Check if region is now fully selected
      const region = findRegion(regionId, regions)
      const isRegionFull =
        region?.branches.every((b) => newBranchIds.includes(b.id)) ?? false

      let newRegionIds = selection.selectedRegions.filter(
        (id) => id !== regionId
      )
      if (isRegionFull) {
        newRegionIds.push(regionId)
      }

      updateSelection({
        selectedRegions: newRegionIds,
        selectedBranches: newBranchIds,
        selectedBranchNames: newBranchNames,
        branchType: 'regular',
        predefinedSelection: null,
      })
    },
    [selection, regions, updateSelection]
  )

  useEffect(() => {
    if (
      selectableRegions.length === 0 ||
      selection.selectedBranches.length === 0
    )
      return

    const firstBranchId = selection.selectedBranches[0]
    const region = findBranchRegion(firstBranchId, selectableRegions)

    if (region && !expandedRegions.has(region.id)) {
      setExpandedRegions((prev) => new Set([...prev, region.id]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableRegions])

  const toggleRegionExpansion = useCallback((regionId: number) => {
    setExpandedRegions((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(regionId)) newSet.delete(regionId)
      else newSet.add(regionId)
      return newSet
    })
  }, [])

  return {
    selection,
    expandedRegions,
    handlePredefinedSelect,
    handleRegionSelect,
    handleBranchSelect,
    toggleRegionExpansion,
  }
}
